import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { BusinessType, ID } from '@shared/types';
import { buildBaseFields, buildUpdateFields, fromSnapshot, writeAuditLog } from '@shared/utils';

const businessTypeCreateSchema = z.object({
  name: z.string().min(1, '業種名は必須です'),
  label: z.string().default(''),
});

export type BusinessTypeCreateInput = z.infer<typeof businessTypeCreateSchema>;
export type BusinessTypeUpdateInput = Partial<BusinessTypeCreateInput>;

const COL = 'businessTypes';

export async function fetchBusinessTypes(): Promise<BusinessType[]> {
  const q = query(
    collection(db, COL),
    where('isDeleted', '==', false),
    orderBy('name')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as BusinessType);
}

export async function fetchBusinessType(id: ID): Promise<BusinessType | null> {
  const snap = await getDoc(doc(db, COL, id));
  return fromSnapshot<BusinessType>(snap);
}

export async function createBusinessType(
  data: BusinessTypeCreateInput,
  userId: ID
): Promise<BusinessType> {
  const validated = parseOrThrow(businessTypeCreateSchema, data);
  const payload = { ...validated, ...buildBaseFields(userId) };
  const ref = await addDoc(collection(db, COL), payload);
  const created = { id: ref.id, ...payload } as unknown as BusinessType;
  await writeAuditLog({
    entityType: 'businessType',
    entityId: ref.id,
    action: 'create',
    userId,
    after: validated,
  });
  return created;
}

export async function updateBusinessType(
  id: ID,
  data: BusinessTypeUpdateInput,
  userId: ID
): Promise<BusinessType> {
  const validated = businessTypeCreateSchema.partial().parse(data);
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('業種が見つかりません。');
  const before = snap.data();
  const patch = { ...validated, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  await writeAuditLog({
    entityType: 'businessType',
    entityId: id,
    action: 'update',
    userId,
    before,
    after: validated,
  });
  return { id, ...before, ...patch } as unknown as BusinessType;
}

export async function deleteBusinessType(id: ID, userId: ID): Promise<void> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('業種が見つかりません。');
  await updateDoc(ref, { isDeleted: true, ...buildUpdateFields(userId) });
  await writeAuditLog({
    entityType: 'businessType',
    entityId: id,
    action: 'delete',
    userId,
    before: snap.data(),
  });
}
